/**
 * Step 8 — Floor Plan Sketch wrapper. Reuses existing FloorPlanSketch module.
 * Lock-on-confirm prompt: "Are you satisfied with this sketch? You cannot edit after confirming."
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Layout, Lock, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import FloorPlanSketch from "@/components/FloorPlanSketch";

export default function Step8FloorPlan({ workflow, request, requestId, onAdvance }) {
  const locked = !!workflow?.step_8?.locked_at;
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const confirmLock = async () => {
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/8/lock`);
      toast.success("Floor plan locked");
      onAdvance(9);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not lock"); }
    finally { setSaving(false); setConfirming(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-8-form">
      <div className="flex items-center gap-2">
        <Layout className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 8 · Floor Plan Sketch</h2>
        {locked && <span className="ml-auto inline-flex items-center gap-1 text-emerald-700 text-xs"><Lock className="w-3 h-3" /> Locked</span>}
      </div>

      <Card>
        <CardContent className="pt-2 pb-2 px-1">
          <FloorPlanSketch requestId={requestId} readOnly={locked} jobInfo={{ code: request?.request_code }} />
        </CardContent>
      </Card>

      {!locked && (
        <>
          {!confirming ? (
            <Button onClick={() => setConfirming(true)} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="step-8-confirm-prompt-btn">
              <Lock className="w-4 h-4 mr-1" /> Confirm Sketch & Lock
            </Button>
          ) : (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex gap-2 items-start mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-xs text-zinc-700 flex-1">
                    Are you satisfied with this sketch? <strong>You cannot edit after confirming.</strong>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => setConfirming(false)} variant="outline" className="h-11" data-testid="step-8-edit-sketch">
                    Edit Sketch
                  </Button>
                  <Button onClick={confirmLock} disabled={saving} className="h-11 bg-emerald-600 text-white"
                          data-testid="step-8-confirm-sketch">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Lock"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {locked && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs font-semibold text-zinc-900">Sketch locked at {new Date(workflow.step_8.locked_at).toLocaleString()}</p>
            <Button onClick={() => onAdvance(9)} className="mt-3 h-11 bg-zinc-900 text-white" data-testid="step-8-next">
              Continue to Step 9
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
